import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../../api/axios";
import SettingsDimensionInputs from "./PanelSettingsComponents/SettingsDimensionInputs";
import FlexibleOptionsInputs from "./PanelSettingsComponents/FlexibleOptionsInputs";
import SpaceOptionsInputs from "./PanelSettingsComponents/SpaceOptionsInputs";
import { SettingGroup, SettingField, ExampleBlock } from "./PanelSettingsComponents/SettingField";
import { OrientationDiagram } from "./PanelSettingsComponents/SettingsDiagrams";
import CustomSelect from "../../Common/CustomSelect";
import SaveStatus from "../../Common/SaveStatus";
import PlaceholderDuctHint from "../../Common/PlaceholderDuctHint";
import { useCurrentTenant } from "../../../hooks/useCurrentTenant";
import { useDebouncedAutosave } from "../../../hooks/useDebouncedAutosave";
import { isEmptyId, EMPTY_GUID } from "../../../builders/dummyDucts";
import { ORIENTATION, ORIENTATION_OPTIONS, normalizeOrientation } from "../../../builders/layoutMode";
import "./ScenarioSettingsPanel.css";

// One sentence per orientation, shown under the dropdown
const ORIENTATION_HINTS = {
  [ORIENTATION.SECTION]:
    "Every section gets its own full-width rows, one below the other. Simple and predictable, but uses the most height.",
  [ORIENTATION.SPACE]:
    "Sections stay in rows, but the next section may fill the free end of a row.",
  [ORIENTATION.FLEXIBLE]:
    "Sections share bands side by side; devices can also sit on vertical rails, with settings per device in the layout tree.",
};

const toUiSettings = (settings) => ({
  ...settings,
  orientation: normalizeOrientation(settings?.orientation),
});

// "" (= "None" in the dropdown) must be sent as the empty Guid, the backend
// cannot deserialize "" into a Guid.
const toApiSettings = (settings) => ({
  ...settings,
  ductsEnclosureId: isEmptyId(settings.ductsEnclosureId) ? EMPTY_GUID : settings.ductsEnclosureId,
  ductsSectionsId: isEmptyId(settings.ductsSectionsId) ? EMPTY_GUID : settings.ductsSectionsId,
});

const ScenarioSettingsPanel = ({ scenario, onScenarioUpdated }) => {
  const currentTenantId = useCurrentTenant();
  const [settings, setSettings] = useState(null);
  const settingsRef = useRef(null);
  const [ducts, setDucts] = useState([]);
  const [ductsLoaded, setDuctsLoaded] = useState(false);

  // Latest values for the save callback (it runs debounced / after unmount)
  const scenarioRef = useRef(scenario);
  scenarioRef.current = scenario;
  const onUpdatedRef = useRef(onScenarioUpdated);
  onUpdatedRef.current = onScenarioUpdated;

  // ----------------------------------------
  // Autosave: only the settings, via PUT /scenario/{id}/settings, so it can
  // never overwrite name/description/sections saved elsewhere.
  // ----------------------------------------
  const { status, schedule } = useDebouncedAutosave(async (nextSettings) => {
    const current = scenarioRef.current;
    if (!current?.id) return;
    const apiSettings = toApiSettings(nextSettings);
    await api.put(`/scenario/${current.id}/settings`, apiSettings);
    onUpdatedRef.current?.({ ...current, settings: apiSettings });
  }, 800);

  // ----------------------------------------
  // Init local settings only when a different scenario is opened.
  // (Our own saves update the parent's scenario too - re-initialising then
  // would throw away what the user typed while the request was running.)
  // ----------------------------------------
  useEffect(() => {
    if (!scenario) return;
    const ui = toUiSettings(scenario.settings ?? {});
    settingsRef.current = ui;
    setSettings(ui);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario?.id]);

  // Duct master data (once)
  useEffect(() => {
    let alive = true;
    api.get("/duct")
      .then((res) => {
        if (!alive) return;
        setDucts(res.data || []);
        setDuctsLoaded(true);
      })
      .catch((err) => console.error("Failed to load ducts:", err));
    return () => { alive = false; };
  }, []);

  const tenantDucts = useMemo(
    () => ducts.filter((d) => d.tenantId === currentTenantId),
    [ducts, currentTenantId]
  );

  // ----------------------------------------
  // Every change goes through here -> state + autosave
  // ----------------------------------------
  const updateSettings = (patchOrFn) => {
    const prev = settingsRef.current ?? {};
    const next = typeof patchOrFn === "function" ? patchOrFn(prev) : { ...prev, ...patchOrFn };
    settingsRef.current = next;
    setSettings(next);
    schedule(next);
  };

  const handleChange = (field, value) => updateSettings({ [field]: value });

  // Space Oriented options
  const handlePlacementOptionChange = (field, val) =>
    updateSettings((prev) => ({
      ...prev,
      placementOptions: {
        ...(prev.placementOptions ?? {}),
        [field]: val,
      },
    }));

  // Flexible Oriented options (separate object, never shared with Space Oriented)
  const handleFlexibleOptionChange = (field, val) =>
    updateSettings((prev) => ({
      ...prev,
      flexibleOptions: {
        ...(prev.flexibleOptions ?? {}),
        [field]: val,
      },
    }));

  // ----------------------------------------
  // Placeholder hint
  // ----------------------------------------
  const usesPlaceholder = (id) =>
    ductsLoaded &&
    (isEmptyId(id) || !tenantDucts.some((d) => String(d.id) === String(id)));

  const enclosurePlaceholder = settings ? usesPlaceholder(settings.ductsEnclosureId) : false;
  const sectionsPlaceholder = settings ? usesPlaceholder(settings.ductsSectionsId) : false;

  // ----------------------------------------
  // Duct options ("None" = use placeholder duct)
  // ----------------------------------------
  const ductOptions = useMemo(() => [
    { value: "", label: "– None (placeholder duct) –" },
    ...tenantDucts.map((d) => ({
      value: String(d.id),
      label: `${d.name} (${d.manufacturer || "?"}) – ${d?.size?.width || "?"}×${d?.size?.height || "?"}mm`,
    })),
  ], [tenantDucts]);

  if (!settings) return <div>Loading settings…</div>;

  return (
    <div className="scenario-settings-container">
      <div className="ssp-status-row">
        <SaveStatus status={status} />
      </div>

      <PlaceholderDuctHint enclosure={enclosurePlaceholder} sections={sectionsPlaceholder} />

      {/* ── Orientation first: it decides which settings below matter ── */}
      <SettingGroup
        title="Layout Design"
        intro="How devices are arranged on the mounting plate. The settings below depend on it."
      >
        <SettingField
          id="ssp-orientation"
          label="Arrangement"
          stacked
          hint={ORIENTATION_HINTS[settings.orientation]}
          example={
            <ExampleBlock diagram={<OrientationDiagram />}>
              The same three sections A, B and C. <b>Section:</b> each on its own rows.{" "}
              <b>Space:</b> B fills the free end of A&rsquo;s row. <b>Flexible:</b> A (in two
              rows), B and C (terminals on a vertical rail) share one band.
            </ExampleBlock>
          }
        >
          <CustomSelect
            label={null}
            value={settings.orientation}
            onChange={(val) => handleChange("orientation", val)}
            options={ORIENTATION_OPTIONS}
            maxHeight={250}
          />
        </SettingField>
      </SettingGroup>

      {/* ── Ducts + dimensions (all orientations) ── */}
      <SettingsDimensionInputs
        orientation={settings.orientation}
        ductOptions={ductOptions}
        selectedVerticalDuctRefId={isEmptyId(settings.ductsEnclosureId) ? "" : String(settings.ductsEnclosureId)}
        onVerticalDuctRefChange={(val) => handleChange("ductsEnclosureId", val)}
        selectedTopDuctRefId={isEmptyId(settings.ductsSectionsId) ? "" : String(settings.ductsSectionsId)}
        onTopDuctRefChange={(val) => handleChange("ductsSectionsId", val)}
        toleranceSection={settings.tolerance}
        onToleranceSectionChange={(e) => handleChange("tolerance", Number(e.target.value))}
        fillMountingSpaceCabinet={settings.fillMountingSpace}
        onFillMountingSpaceChange={(e) => handleChange("fillMountingSpace", Number(e.target.value))}
        minDistanceWireDuctCabinet={settings.minDistanceWireDuct}
        onMinDistanceWireDuctChange={(e) => handleChange("minDistanceWireDuct", Number(e.target.value))}
      />

      {/* ── Options of the selected orientation ── */}
      {settings.orientation === ORIENTATION.SPACE && (
        <SpaceOptionsInputs
          placementOptions={settings.placementOptions}
          onChange={handlePlacementOptionChange}
          fillMountingSpace={settings.fillMountingSpace ?? 0}
        />
      )}

      {settings.orientation === ORIENTATION.FLEXIBLE && (
        <FlexibleOptionsInputs
          flexibleOptions={settings.flexibleOptions}
          onChange={handleFlexibleOptionChange}
        />
      )}
    </div>
  );
};

export default ScenarioSettingsPanel;
